import { Component, computed, effect, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOption, OnyxOptionsGroup } from '@onyx/angular';
import { OnyxDropdownComponent } from '@onyx/angular';
import { startWith, switchMap } from 'rxjs';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { EMPLOYEE_ROLE_LEVELS } from '../../constants/employee-role-levels';
import type { EmployeeRole } from '../../enums/employee-role';
import { EmployeeHelper } from '../../helpers/employee.helper';

@Component({
  selector: 'app-employee-roles-dropdown',
  imports: [OnyxDropdownComponent, ReactiveFormsModule],
  templateUrl: './employee-roles-dropdown.component.html',
})
export class EmployeeRolesDropdownComponent {
  private readonly translateService = inject(TranslateService);

  public readonly control =
    input.required<FormControl<EmployeeRole[] | null>>();

  protected readonly I18N = 'employees.roles';

  private readonly roles = toSignal(
    inject(DictionariesService).getAvailableEmployeeRoles(),
  );
  private readonly value = toSignal(
    toObservable(this.control).pipe(
      switchMap((control) =>
        control.valueChanges.pipe(startWith(control.value)),
      ),
    ),
  );

  protected options = computed((): OnyxOptionsGroup<EmployeeRole>[] => {
    const roles = this.roles() ?? [];
    const value = this.value() ?? [];

    return EMPLOYEE_ROLE_LEVELS.map((level) => ({
      id: level.level,
      subheading: `${this.I18N}.groups.${level.level}`,
      options: roles
        .filter((role) => level.roles.includes(role.value))
        .map((role): OnyxOption<EmployeeRole> => ({
          name: role.name,
          value: role.value,
          implied: !!EmployeeHelper.getCoveringRole(role.value, value),
          description:
            level.hint &&
            this.translateService.instant(`${this.I18N}.${level.hint}`),
        })),
    })).filter((group) => group.options.length);
  });

  constructor() {
    effect(() => {
      const value = this.value();
      if (!value) return;

      const uncovered = value.filter(
        (role) => !EmployeeHelper.getCoveringRole(role, value),
      );
      if (uncovered.length !== value.length) {
        this.control().setValue(uncovered);
      }
    });
  }
}
